import { appConfigStore } from 'Models/AppConfigStore';
import { getFullName } from 'Util/StringUtils';

export interface CustomerNameParts {
	firstName?: string;
	lastName?: string;
	companyName?: string;
}

/**
 * Resolves the customer name shown on staff-facing surfaces (check-in, booking detailed view,
 * global search, the staff bookings list, the admin users table, the scanner result).
 *
 * When the `Customer.displayCompanyNameInsteadOfName` config is enabled and the customer has a
 * non-blank company name, the company name is returned in place of the personal name. Otherwise the
 * personal full name is returned (via {@link getFullName}, so its fallbacks/capitalisation apply).
 *
 * This is a staff-UI display concern only — reports keep both the personal name and a separate
 * company column and must not use this helper.
 */
export function getCustomerDisplayName(
	customer: CustomerNameParts | null | undefined,
	showLastNameFirst = false,
): string | null {
	const companyName = customer?.companyName?.trim();
	if (appConfigStore.displayCompanyNameInsteadOfName && companyName) {
		return companyName;
	}
	return getFullName(customer?.firstName, customer?.lastName, showLastNameFirst);
}

/**
 * Appends the customer's company name in brackets after a display name when they have one
 * (e.g. "John Smith" → "John Smith (Amity Trader)"). Used on staff check-in surfaces to surface the
 * company alongside the driver / booking name. Additive — shown whenever a company is present,
 * independent of the "display company instead of name" config.
 */
export function appendCompanyName(
	name: string | null | undefined,
	companyName: string | null | undefined,
): string {
	const company = companyName?.trim();
	const base = (name ?? '').trim();
	if (!company) {
		return base;
	}
	return base ? `${base} (${company})` : `(${company})`;
}
